'use client';

import React, { useCallback, useContext, useEffect, useId, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@tranquility-ai/theme-components/button';
import { Input } from '@tranquility-ai/theme-components/input';
import { Modal } from '@tranquility-ai/theme-components/modal';
import { SegmentedControl } from '@tranquility-ai/theme-components/segmented-control';
import { ToastContext } from '@/app/contexts';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { ToastLevel } from '@/components/ui/toast';
import { caseManagementKeys } from '@/hooks/useCaseManagement';
import { useCasesList } from '@/hooks/useCasesList';
import { normalizeNewCaseNameInput, validateNewCaseName } from '@/shared/case-file-management';
import { useCaseCreationFlowsEnabled } from '@/shared/case-creation-flag';
import {
  createCase,
  PermissionServiceError,
  type CreatedCase,
} from '@/shared/cases-permission-client';
import { provenanceKeys } from '@/shared/cors-provenance-api';

/**
 * Shared create-case modal (TR-4241 / TR-4384).
 *
 * Talks to edge-lambda via {@link createCase}. edge-lambda asks
 * permission-service for the case row and writes the Manual prefix from the
 * returned `folderSegment`. That segment is the only safe value for later
 * object writes.
 *
 * Mounted behind `case-creation-flows` and `casesFromPermissionService`.
 */
type NewCaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The created or already-existing case. Select it by `name`. */
  onCaseCreated: (created: CreatedCase) => void;
  initialName?: string;
  /**
   * Whether per-case access is enforced. Only Case Management can know this:
   * the tenant access setting is a CSM route, and a regular user gets 403.
   * Omit it from the header picker and Case Files. The choice stays hidden
   * and permission-service applies its own default (only me when enforcement
   * is on).
   */
  enforcementActive?: boolean;
};

type AccessChoice = 'only-me' | 'everyone';

export function NewCaseModal({
  isOpen,
  onClose,
  onCaseCreated,
  initialName = '',
  enforcementActive = false,
}: NewCaseModalProps) {
  const t = useTimePilotT();
  const enabled = useCaseCreationFlowsEnabled();
  const casesQuery = useCasesList();
  const queryClient = useQueryClient();
  const { setToast, setToastOpen } = useContext(ToastContext);
  const nameHintId = useId();
  const nameErrorId = useId();
  const [caseName, setCaseName] = useState(initialName);
  const [access, setAccess] = useState<AccessChoice>('only-me');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCaseName(initialName);
    setAccess('only-me');
    setFieldError(null);
    setSubmitError(null);
    setIsCreating(false);
  }, [initialName, isOpen]);

  const showToast = useCallback(
    (title: string, description: string) => {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 4000,
        title,
        description,
        level: ToastLevel.SUCCESS,
      });
    },
    [setToast, setToastOpen],
  );

  const handleDismiss = useCallback(() => {
    if (isCreating) return;
    onClose();
  }, [isCreating, onClose]);

  const handleCreate = useCallback(async () => {
    const validationError = validateNewCaseName(caseName, casesQuery.data ?? []);
    if (validationError) {
      setFieldError(validationError);
      return;
    }
    const trimmedName = normalizeNewCaseNameInput(caseName);
    setIsCreating(true);
    setSubmitError(null);
    setFieldError(null);

    try {
      const created = await createCase({
        name: trimmedName,
        openToAll: enforcementActive === true && access === 'everyone',
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: provenanceKeys.cases() }),
        queryClient.invalidateQueries({ queryKey: caseManagementKeys.all }),
      ]);
      showToast(
        created.created ? t('caseManagement.created') : t('caseManagement.alreadyExists'),
        created.created
          ? t('caseManagement.createdReady', { name: created.name })
          : t('caseManagement.alreadyOpened', { name: created.name }),
      );
      onCaseCreated(created);
      onClose();
    } catch (error) {
      if (error instanceof PermissionServiceError && error.status === 409) {
        setFieldError(t('caseManagement.duplicateName', { name: trimmedName }));
      } else {
        setSubmitError(
          error instanceof Error && error.message
            ? error.message
            : t('caseManagement.createFailed'),
        );
      }
    } finally {
      setIsCreating(false);
    }
  }, [
    access,
    caseName,
    casesQuery.data,
    enforcementActive,
    onCaseCreated,
    onClose,
    queryClient,
    showToast,
    t,
  ]);

  if (!enabled) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      onOpenChange={(open) => {
        if (!open) handleDismiss();
      }}
      size="md"
      title={t('caseManagement.createTitle')}
      bodyClassName="pr-4"
      dismissOnBackdropClick={!isCreating}
      dismissOnEscape={!isCreating}
      showCloseButton={!isCreating}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button
            type="button"
            style="text"
            color="light"
            background="inverse"
            onClick={handleDismiss}
            disabled={isCreating}
          >
            {t('timeline.dialog.cancel')}
          </Button>
          <Button
            type="button"
            style="solid"
            color="accent"
            background="inverse"
            onClick={() => void handleCreate()}
            disabled={isCreating}
            isLoading={isCreating}
            loadingText={t('caseManagement.creating')}
          >
            {t('caseManagement.createCase')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
            label={t('caseManagement.caseName')}
          value={caseName}
          background="inverse"
          className="inverse-form-surface w-full"
          disabled={isCreating}
          invalid={Boolean(fieldError)}
          error={fieldError ?? undefined}
          aria-describedby={fieldError ? `${nameHintId} ${nameErrorId}` : nameHintId}
            placeholder={t('caseManagement.enterCaseName')}
          onChange={(event) => {
            setCaseName(event.target.value);
            setFieldError(null);
            setSubmitError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !isCreating) {
              event.preventDefault();
              void handleCreate();
            }
          }}
        />
        <div
          id={nameHintId}
          className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-[0.8125rem] leading-[1.45] text-white/60"
        >
          <ul className="list-disc space-y-0.5 pl-5">
              <li>{t('caseManagement.nameRules')}</li>
              <li>{t('caseManagement.cannotBegin')}</li>
          </ul>
        </div>
        {enforcementActive ? (
          <SegmentedControl
            aria-label={t('caseManagement.whoCanOpen')}
            size="lg"
            variant="dark"
            value={access}
            onValueChange={(value) => setAccess(value as AccessChoice)}
            options={[
              { value: 'only-me', label: t('caseManagement.onlyMe') },
              { value: 'everyone', label: t('caseManagement.openToAll') },
            ]}
          />
        ) : null}
        {submitError ? (
          <p id={nameErrorId} role="alert" className="text-sm text-red-300">
            {submitError}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
